import { FlatList, View, TouchableOpacity } from "react-native";
import { useEffect, useState } from "react";
import { palladium, spacing_s } from "../../StyleHelpers";
import { AsyncStorageKeys, Pages } from "../../Types";
import { accountCredits, accountCreditsExpiration } from "../../services/accountService";
import {
	ProfileNameSelectScreenNavigationProp,
	RecoverPasswordScreenNavigationProp,
	RedeemVoucherScreenNavigationProp,
	routeDataManagementSettings,
	routeProfileNameSelect,
	routeRecoverPassword,
	routeRedeemVoucher,
	routeTitleDetails,
	SettingsDataManagementNavigationProp,
	TitleDetailsScreenNavigationProp,
} from "../../Routes";
import { formatDate, getTranslatedDeviceName, globalAny, historyFormat } from "../../utils/Utils";
import { useNavigation } from "@react-navigation/native";
import * as CacheStorageUtils from "../../utils/CacheStorageUtils";
import LabelContainer from "../../components/SettingsComponent/labelContainer";
import GroupContainer from "../../components/SettingsComponent/groupContainer";
import SettingsContainer from "../../components/Layout/SettingsContainer";
import PlaylistTitle from "../../components/PlaylistTitle";
import { purchaseHistory } from "../../services/paymentService";
import { TransactionHistory } from "../../models/TransactionHistory";
import { PurchaseHistory } from "../../models/PurchaseHistory";
import TransactionHistoryLoader from "../../components/Loaders/TransactionHistoryLoader";
import H5 from "../../components/Typography/H5";
import { removeCacheItem } from "../../utils/CacheStorageUtils";
import usePaymentTransactionStore from "../../store/usePaymentTransaction.store";
import useGenericContentStore from "../../store/genericContent.store";
import { StyledAccount, StyledContainerAccount, StyledNoTransaction, StyledTransaction } from "./styled";
import SettingsLoader from "../../components/Loaders/SettingsLoader";

const SettingAccount = () => {
	const [accountEmail, setAccountEmail] = useState("");
	const [deviceName, setDeviceName] = useState("");
	const [credits, setCredits] = useState("");
	const [creditsExpirationText, setCreditsExpirationText] = useState("");
	const [transactionHistory, setTransactionHistory] = useState<TransactionHistory[]>([]);
	const [history, setHistory] = useState<PurchaseHistory[]>([]);
	const [transactionResponse, setTransactionResponse] = useState();
	const navigation = {
		titleScreen: useNavigation<TitleDetailsScreenNavigationProp>(),
		recoverPassword: useNavigation<RecoverPasswordScreenNavigationProp>(),
		redeemVoucher: useNavigation<RedeemVoucherScreenNavigationProp>(),
		settingsDataManagement: useNavigation<SettingsDataManagementNavigationProp>(),
		profileNameSelect: useNavigation<ProfileNameSelectScreenNavigationProp>(),
	};
	const { setTransactionValue } = usePaymentTransactionStore((state: any) => ({
		setTransactionValue: state.setTransactionValue,
	}));
	const { setNavigateEvent } = useGenericContentStore((state: any) => ({
		setNavigateEvent: state.setNavigateEvent,
	}));

	/**
	 * Initializes user account data by fetching credits, expiration dates, and purchase history concurrently.
	 *
	 * - Uses `Promise.allSettled` to run multiple async calls in parallel and handle their results safely.
	 * - Caches the combined account details for offline or future use.
	 * - Updates state with user credits and credit expiration information:
	 *   - Formats and sorts expiration dates, showing the earliest expiration or a fallback message.
	 * - Sets transaction response code and formats transaction history for UI display.
	 * - In case of any error during state updates, clears the cached account details to avoid stale data.
	 */
	const Init = async () => {
		let userCredits: any;
		let userCreditsExpiration: any;
		let userTransactionHistory: any;

		const promiseResult = await Promise.allSettled([accountCredits(), accountCreditsExpiration(), purchaseHistory()]);
		const [userCreditsData, userCreditsExpirationData, purchaseHistoryData] = promiseResult.map((i: any) => i.value);

		CacheStorageUtils.setCacheItem(AsyncStorageKeys.accountDetails, [userCreditsData, userCreditsExpirationData, purchaseHistoryData], true);

		userCredits = userCreditsData;
		userCreditsExpiration = userCreditsExpirationData;
		userTransactionHistory = purchaseHistoryData;

		try {
			if (userCredits) {
				setCredits(`${userCredits.creditsAvailable} ${globalAny.language.credit_balance}`);
			}

			if (userCreditsExpiration) {
				let sortDate = userCreditsExpiration?.creditDates
					?.map((obj: any) => {
						return { ...obj, date: new Date(obj?.date) };
					})
					.sort((a: any, b: any) => a?.date - b?.date);

				if (sortDate[0]?.date) {
					setCreditsExpirationText(globalAny.language.expires_on + " " + formatDate(new Date(sortDate[0]?.date)));
				} else {
					setCreditsExpirationText(globalAny.language.redeem_voucher_credit);
				}
			}

			setTransactionResponse(userTransactionHistory?.responseCode);

			if (userTransactionHistory) {
				setHistory(userTransactionHistory?.purchases);
				setTransactionHistory(historyFormat(userTransactionHistory?.purchases));
				setTransactionValue(historyFormat(userTransactionHistory?.purchases));
			}
		} catch {
			removeCacheItem(AsyncStorageKeys.accountDetails);
		}
	};

	/**
	 * Navigates to the password recovery screen with a reset action.
	 */
	const onResetPassword = () => {
		navigation.recoverPassword.navigate(routeRecoverPassword, { settings: Pages.reset });
	};

	/**
	 * Navigates to the profile name selection screen with a reference to the settings page.
	 */
	const onEditDeviceName = () => {
		navigation.profileNameSelect.navigate(routeProfileNameSelect, { location: Pages.settings });
	};

	/**
	 * Navigates to the voucher redemption screen.
	 */
	const onRedeemVoucher = () => {
		navigation.redeemVoucher.navigate(routeRedeemVoucher, {});
	};

	/**
	 * Navigates to the transaction history screen only if there is transaction history available.
	 */
	const onTransactionHistory = () => {
		if (history?.length !== 0) navigation.settingsDataManagement.navigate(routeDataManagementSettings, { location: Pages.transactions });
	};

	/**
	 * Handles the press event on an item by setting the navigation event
	 * and navigating to the title details screen with the given parentProductId.
	 *
	 * @param {number} parentProductId - The ID of the parent product to navigate to.
	 */
	const onPress = (parentProductId: number) => {
		setNavigateEvent(Pages.transactions);
		navigation.titleScreen.navigate(routeTitleDetails, { parentProductId: parentProductId });
	};

	/**
	 * Renders an individual item in a list with a touchable label.
	 * When pressed, it triggers the onPress handler with the item's parentProductId.
	 *
	 * @param {object} param0 - The render item props containing the data item.
	 * @param {any} param0.item - The item to render.
	 * @returns {JSX.Element} - The rendered item component.
	 */
	const renderItem = ({ item }: any) => {
		return (
			<View style={{ paddingBottom: spacing_s }}>
				<TouchableOpacity onPress={() => onPress(item.parentProductId)}>
					<LabelContainer addMarginChild={true} header={item.title} body={item.details} />
				</TouchableOpacity>
			</View>
		);
	};

	/**
	 * On component mount:
	 * - Retrieves stored user email and device name from localStorage and updates state if available.
	 * - Calls the async `Init` function to initialize account-related data.
	 *
	 * On component unmount:
	 * - Clears user-related state values to reset the component state.
	 */
	useEffect(() => {
		const userEmail = localStorage.getItem(AsyncStorageKeys.email);
		const userDeviceName = localStorage.getItem(AsyncStorageKeys.profileName);

		if (userEmail && userDeviceName) {
			setAccountEmail(userEmail);
			setDeviceName(userDeviceName);
		}

		Init();

		return () => {
			setAccountEmail("");
			setDeviceName("");
			setCreditsExpirationText("");
		};
	}, []);

	return (
		//  @ts-ignore
		<StyledAccount>
			{(!credits && !creditsExpirationText) || !transactionHistory ? (
				<SettingsLoader />
			) : (
				<>
					<SettingsContainer>
						<GroupContainer
							header={globalAny.language.my_account}
							body={accountEmail}
							buttonText={globalAny.language.change_password}
							onPress={onResetPassword}
						/>

						<GroupContainer
							header={globalAny.language.device_name}
							body={getTranslatedDeviceName(deviceName)}
							buttonText={globalAny.language.edit}
							onPress={onEditDeviceName}
							hideButton={localStorage.getItem(AsyncStorageKeys.isInCarScreen) === "true"}
						/>
					</SettingsContainer>

					<SettingsContainer>
						<GroupContainer
							header={credits}
							body={creditsExpirationText}
							buttonText={globalAny.language.redeem_voucher}
							onPress={onRedeemVoucher}
						/>
					</SettingsContainer>

					<SettingsContainer>
						{/* @ts-ignore */}
						<StyledContainerAccount>
							<TouchableOpacity onPress={onTransactionHistory}>
								{/* @ts-ignore */}
								<StyledTransaction>
									<PlaylistTitle onPress={onTransactionHistory} title={globalAny.language.transaction_history} noSpacing />
								</StyledTransaction>
							</TouchableOpacity>

							{transactionHistory.length > 0 && (
								<FlatList
									keyExtractor={(item) => item.title}
									data={transactionHistory.slice(0, 3)}
									extraData={transactionHistory.slice(0, 3)}
									renderItem={renderItem}
									showsVerticalScrollIndicator={false}
									showsHorizontalScrollIndicator={false}
								/>
							)}

							{transactionResponse !== 10000 && <TransactionHistoryLoader />}

							{history?.length === 0 && transactionResponse === 10000 ? (
								//@ts-ignore
								<StyledNoTransaction>
									<H5 fontColor={palladium}>{globalAny.language.no_transaction_history}</H5>
								</StyledNoTransaction>
							) : (
								<></>
							)}
						</StyledContainerAccount>
					</SettingsContainer>
				</>
			)}
		</StyledAccount>
	);
};

export default SettingAccount;
